import type { AuditEventResponse } from "@esi/shared";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { useSearch } from "../router.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { FigureTable } from "./reports/parts.tsx";

const PAGE = 50;

/**
 * The audit events of one action, linked from the reports (tenant admins: the server answers
 * `403` to anyone else). When, what, on which kind of target and by which kind of actor; never
 * the event's details or reasons.
 */
export function AuditList() {
  const { admin } = useAdminApi();
  const { t } = useTranslation();
  const action = useSearch().get("action") ?? "";
  const events = useInfiniteQuery({
    queryKey: ["admin", "audit", action],
    enabled: action !== "",
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<AuditEventResponse[]> =>
      ok(
        await admin.audit.$get({
          query: { action, limit: String(PAGE), ...(pageParam && { before: pageParam }) },
        }),
      ),
    getNextPageParam: (last) => (last.length < PAGE ? undefined : last.at(-1)?.occurredAt),
    retry: false,
  });
  const label = t(`audit.actionLabel.${action.replaceAll(".", "_")}`, { defaultValue: action });
  return (
    <div data-esi-content="people" className="flex flex-col gap-6">
      <PageHeader title={`${t("audit.title")} · ${label}`} lead={t("audit.lead")} />
      {events.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : events.isError ? (
        <Notice tone="danger">{events.error.message}</Notice>
      ) : (
        (() => {
          const rows = events.data.pages.flat();
          return rows.length === 0 ? (
            <p className="text-sm text-fg-muted">{t("audit.empty")}</p>
          ) : (
            <>
              <FigureTable
                caption={label}
                columns={[t("audit.when"), t("audit.action"), t("audit.target"), t("audit.actor")]}
                rows={rows.map((e) => ({
                  key: String(e.id),
                  cells: [
                    formatDateTime(e.occurredAt),
                    t(`audit.actionLabel.${e.action.replaceAll(".", "_")}`, {
                      defaultValue: e.action,
                    }),
                    e.targetType ?? "—",
                    t(`audit.actorType.${e.actorType}`),
                  ],
                }))}
              />
              {events.hasNextPage ? (
                <Button
                  variant="outline"
                  className="self-start"
                  disabled={events.isFetchingNextPage}
                  onClick={() => void events.fetchNextPage()}
                >
                  {t("audit.loadMore")}
                </Button>
              ) : null}
            </>
          );
        })()
      )}
    </div>
  );
}
